import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export type BadgeVariant =
	| 'default'
	| 'primary'
	| 'secondary'
	| 'success'
	| 'warning'
	| 'danger'
	| 'info';
type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
	variant?: BadgeVariant;
	size?: BadgeSize;
	pill?: boolean;
	dot?: boolean;
	children: React.ReactNode;
}

// Variant classes will be determined dynamically in the component

const sizeClasses: Record<BadgeSize, string> = {
	xs: 'px-2 py-0.5 text-xs',
	sm: 'px-2.5 py-0.5 text-sm',
	md: 'px-3 py-1 text-sm',
	lg: 'px-3.5 py-1.5 text-base',
};

export function Badge({
	variant = 'default',
	size = 'sm',
	pill = false,
	dot = false,
	className,
	children,
	...props
}: BadgeProps) {
	const colors = useThemeColors();

	const getVariantClasses = () => {
		switch (variant) {
			case 'primary':
				return colors.classes.badgePrimary;
			case 'info':
				return colors.classes.badgeInfo;
			case 'secondary':
				return colors.classes.badgeSecondary;
			case 'default':
				return colors.classes.badgeDefault;
			case 'success':
				return colors.classes.badgeSuccess;
			case 'warning':
				return colors.classes.badgeWarning;
			case 'danger':
				return colors.classes.badgeDanger;
			default:
				return colors.classes.badgeDefault;
		}
	};

	return (
		<span
			className={cn(
				'inline-flex items-center font-medium',
				pill ? 'rounded-full' : 'rounded',
				getVariantClasses(),
				sizeClasses[size],
				className,
			)}
			{...props}
		>
			{dot && (
				<svg
					className={cn(
						'fill-current',
						size === 'xs' || size === 'sm'
							? '-ml-0.5 mr-1.5 h-2 w-2'
							: '-ml-1 mr-2 h-3 w-3',
					)}
					viewBox="0 0 8 8"
				>
					<circle cx="4" cy="4" r="3" />
				</svg>
			)}
			{children}
		</span>
	);
}
